import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, BookOpen } from 'lucide-react';
import { servicesData, serviceImages as defaultServiceImages } from '../data/mock';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const [customImages, setCustomImages] = useState({});
  const [relatedStories, setRelatedStories] = useState([]);

  // Fetch custom service images from backend
  useEffect(() => {
    const fetchServiceImages = async () => {
      try {
        const res = await fetch(`${API_URL}/api/service-images`);
        if (res.ok) {
          setCustomImages(await res.json());
        }
      } catch (e) {
        console.error('Error fetching service images:', e);
      }
    };
    fetchServiceImages();
  }, []);

  // Find the service
  let service = null;
  let category = null;

  servicesData.forEach((cat) => {
    cat.services.forEach((svc) => {
      if (svc.slug === slug) {
        service = svc;
        category = cat;
      }
    });
  });

  // Fetch related stories based on service name
  useEffect(() => {
    const fetchRelatedStories = async () => {
      if (!service || !category) return;
      try {
        const res = await fetch(`${API_URL}/api/stories`);
        if (res.ok) {
          const stories = await res.json();
          const serviceName = service.name.toLowerCase();
          const serviceSlug = service.slug.toLowerCase();
          
          // Broad keyword map for matching story services to this service page
          const keywordMap = {
            'brand-identity': ['brand', 'branding', 'brand launch', 'brand story', 'identity', 'visual identity'],
            'content-creation': ['content', 'social media', 'creative', 'social media strategy'],
            'film-production': ['film', 'video', 'cinematography', 'production', 'photography', 'portrait', 'video production'],
            'event-broadcasting': ['event', 'broadcast', 'live', 'coverage', 'event coverage', 'event photography'],
            'graphic-design': ['design', 'graphics', 'materials', 'photo editing', 'marketing materials'],
            'digital-advertising': ['advertising', 'marketing', 'ads', 'digital'],
            'website-development': ['website', 'web', 'development'],
            'motion-graphics': ['motion', 'animation', 'animated'],
            'seo-strategies': ['seo', 'search', 'optimization'],
            'ai-automations': ['ai', 'automation', 'workflow'],
            'wedding-cinematography': ['wedding', 'ceremony', 'nuptial'],
            'real-estate-media': ['real estate', 'property', 'drone'],
            'content-strategy': ['content strategy', 'editorial', 'content planning'],
            'media-strategy': ['media strategy', 'media planning', 'strategy'],
            'technology-advisory': ['technology', 'tech', 'advisory'],
            'budget-appraisals': ['budget', 'appraisal', 'cost'],
            'automation-consulting': ['automation consulting', 'process', 'workflow'],
            'audience-audit': ['audience', 'audit', 'analysis'],
          };
          
          const keywords = keywordMap[serviceSlug] || [serviceName];
          
          const related = stories.filter(story => {
            if (!story.services_provided || story.services_provided.length === 0) return false;
            return story.services_provided.some(s => {
              const storyService = s.toLowerCase();
              // Direct name match
              if (storyService.includes(serviceName) || serviceName.includes(storyService)) return true;
              // Keyword match
              return keywords.some(kw => storyService.includes(kw) || kw.includes(storyService));
            });
          });
          setRelatedStories(related);
        }
      } catch (e) {
        console.error('Error fetching related stories:', e);
      }
    };
    fetchRelatedStories();
  }, [slug]);

  // Use custom image if available, otherwise fall back to default
  const defaultImages = defaultServiceImages[slug] || { hero: '', gallery: [] };
  const heroImage = customImages[slug] || defaultImages.hero;

  // Get prev/next services
  const allServices = servicesData.flatMap(cat => cat.services);
  const currentIndex = allServices.findIndex(s => s.slug === slug);
  const prevService = currentIndex > 0 ? allServices[currentIndex - 1] : null;
  const nextService = currentIndex < allServices.length - 1 ? allServices[currentIndex + 1] : null;

  // Scroll to top when slug changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!service) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-serif text-4xl text-white mb-4">Service Not Found</h1>
          <Link to="/services" className="text-white/50 hover:text-white transition-colors">
            Back to Services
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO 
        title={`${service.name} - ${category?.category} Services`}
        description={`${service.fullDescription} Professional ${service.name.toLowerCase()} services by CreativeDirectorKhan | KHAN. Serving clients worldwide.`}
        keywords={`${service.name.toLowerCase()}, ${category?.category.toLowerCase()}, ${service.features?.join(', ')}, creative services, worldwide`}
        canonical={`https://creativedirectorkhan.com/services/${slug}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": service.name,
          "description": `${service.fullDescription} Professional ${service.name.toLowerCase()} services by CreativeDirectorKhan | KHAN.`,
          "url": `https://creativedirectorkhan.com/services/${slug}`,
          "provider": { "@type": "Organization", "name": "CreativeDirectorKhan | KHAN", "url": "https://creativedirectorkhan.com" },
          "areaServed": "Worldwide",
          "category": category?.category
        }}
      />
      {/* Hero Section with Single Image */}
      <section className="relative h-[70vh] min-h-[500px] overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt={service.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/50 to-transparent" />
          <div className="absolute inset-0 bg-black/30" />
        </div>

        {/* Content Overlay */}
        <div className="absolute inset-0 flex items-end z-10">
          <div className="max-w-7xl mx-auto px-8 md:px-12 pb-16 w-full">
            {/* Breadcrumb */}
            <div className="flex items-center gap-3 mb-6">
              <Link 
                to="/services" 
                className="text-white/40 text-xs tracking-[0.2em] uppercase hover:text-white/70 transition-colors"
              >
                Services
              </Link>
              <span className="text-white/20">/</span>
              <span className="text-white/40 text-xs tracking-[0.2em] uppercase">
                {category?.category}
              </span>
            </div>

            <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl font-light text-white mb-4">
              {service.name}
            </h1>
            <p className="text-white/60 text-xl max-w-2xl">
              {service.fullDescription}
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-8 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <p className="text-white/40 text-xs tracking-[0.4em] uppercase mb-6">What&apos;s Included</p>
              <h2 className="font-serif text-4xl md:text-5xl font-light text-white mb-8">
                Service Features
              </h2>
              <p className="text-white/50 text-lg leading-relaxed">
                {service.fullDescription}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {service.features?.map((feature, index) => (
                <div
                  key={index}
                  className="group p-6 border border-white/5 hover:border-white/15 bg-white/[0.02] hover:bg-white/[0.04] transition-all duration-500"
                >
                  <span className="text-white/20 font-serif text-2xl block mb-3">
                    0{index + 1}
                  </span>
                  <span className="text-white/80 group-hover:text-white transition-colors duration-500">
                    {feature}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* A Decade of Excellence Section */}
      <section className="py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-8 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-white/40 text-xs tracking-[0.4em] uppercase mb-6">Experience</p>
              <h2 className="font-serif text-4xl md:text-5xl font-light text-white mb-8">
                A Decade of Excellence
              </h2>
              <div className="space-y-6">
                <p className="text-white/60 text-lg leading-relaxed">
                  For over a decade, creativedirectorkhan has partnered with businesses across industries to elevate their brand presence and achieve measurable results.
                </p>
                <p className="text-white/50 leading-relaxed">
                  From startups to established enterprises, I bring a wealth of experience in crafting compelling narratives, stunning visuals, and strategic campaigns that resonate with audiences and drive growth.
                </p>
                <p className="text-white/50 leading-relaxed">
                  Every project is approached with meticulous attention to detail, creative vision, and an unwavering commitment to excellence—ensuring your brand stands out in today&apos;s competitive landscape.
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-6">
              <div className="p-8 border border-white/10 bg-white/[0.02]">
                <span className="font-serif text-5xl text-white/90 block mb-2">10+</span>
                <span className="text-white/40 text-sm tracking-[0.15em] uppercase">Years Experience</span>
              </div>
              <div className="p-8 border border-white/10 bg-white/[0.02]">
                <span className="font-serif text-5xl text-white/90 block mb-2">200+</span>
                <span className="text-white/40 text-sm tracking-[0.15em] uppercase">Projects Delivered</span>
              </div>
              <div className="p-8 border border-white/10 bg-white/[0.02]">
                <span className="font-serif text-5xl text-white/90 block mb-2">50+</span>
                <span className="text-white/40 text-sm tracking-[0.15em] uppercase">Happy Clients</span>
              </div>
              <div className="p-8 border border-white/10 bg-white/[0.02]">
                <span className="font-serif text-5xl text-white/90 block mb-2">15+</span>
                <span className="text-white/40 text-sm tracking-[0.15em] uppercase">Industry Awards</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 border-t border-white/5 bg-white/[0.01]">
        <div className="max-w-7xl mx-auto px-8 md:px-12">
          <p className="text-white/40 text-xs tracking-[0.4em] uppercase mb-6">How It Works</p>
          <h2 className="font-serif text-4xl md:text-5xl font-light text-white mb-16">
            The Process
          </h2>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Consultation', desc: 'We discuss your vision, goals, and requirements in detail.' },
              { step: '02', title: 'Planning', desc: 'Creating a detailed plan and timeline for your project.' },
              { step: '03', title: 'Execution', desc: 'Bringing your project to life with precision and creativity.' },
              { step: '04', title: 'Delivery', desc: 'Final delivery with revisions until you are satisfied.' }
            ].map((item, index) => (
              <div key={index} className="group">
                <span className="font-serif text-5xl text-white/10 group-hover:text-white/20 transition-colors duration-500 block mb-4">
                  {item.step}
                </span>
                <h3 className="font-serif text-xl text-white/90 mb-2">{item.title}</h3>
                <p className="text-white/40 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-16 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-8 md:px-12">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            {prevService ? (
              <Link
                to={`/services/${prevService.slug}`}
                className="group flex items-center gap-4 text-white/40 hover:text-white transition-colors duration-500"
              >
                <ArrowLeft className="w-5 h-5 group-hover:-translate-x-2 transition-transform duration-500" />
                <div>
                  <p className="text-xs tracking-[0.2em] uppercase mb-1">Previous</p>
                  <p className="font-serif text-xl text-white/70 group-hover:text-white transition-colors duration-500">
                    {prevService.name}
                  </p>
                </div>
              </Link>
            ) : <div />}

            <Link
              to="/services"
              className="text-white/40 text-xs tracking-[0.2em] uppercase hover:text-white transition-colors duration-500"
            >
              All Services
            </Link>

            {nextService ? (
              <Link
                to={`/services/${nextService.slug}`}
                className="group flex items-center gap-4 text-white/40 hover:text-white transition-colors duration-500 text-right"
              >
                <div>
                  <p className="text-xs tracking-[0.2em] uppercase mb-1">Next</p>
                  <p className="font-serif text-xl text-white/70 group-hover:text-white transition-colors duration-500">
                    {nextService.name}
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform duration-500" />
              </Link>
            ) : <div />}
          </div>
        </div>
      </section>

      {/* Related Success Stories Section */}
      {relatedStories.length > 0 && (
        <section className="py-24 border-t border-white/5">
          <div className="max-w-7xl mx-auto px-8 md:px-12">
            <div className="flex items-center justify-between mb-12">
              <div>
                <p className="text-white/40 text-xs tracking-[0.4em] uppercase mb-4">Our Work</p>
                <h2 className="font-serif text-3xl md:text-4xl font-light text-white">
                  Success Stories
                </h2>
              </div>
              <Link 
                to="/stories"
                className="hidden md:flex items-center gap-2 text-white/50 hover:text-white text-sm transition-colors"
              >
                View All Stories
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedStories.slice(0, 3).map((story) => (
                <Link 
                  key={story.slug}
                  to={`/stories/${story.slug}`}
                  className="group block border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition-all overflow-hidden"
                >
                  {/* Story Image */}
                  <div className="aspect-[16/9] relative overflow-hidden">
                    {story.hero_image ? (
                      <img 
                        src={story.hero_image} 
                        alt={story.client_name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center">
                        <BookOpen className="w-12 h-12 text-white/20" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors" />
                  </div>
                  
                  {/* Story Info */}
                  <div className="p-6">
                    <p className="text-white/40 text-xs tracking-wider uppercase mb-2">Success Story</p>
                    <h3 className="font-serif text-xl text-white mb-2 group-hover:text-white/80 transition-colors">
                      {story.client_name}
                    </h3>
                    <p className="text-white/50 text-sm line-clamp-2">{story.title}</p>
                  </div>
                </Link>
              ))}
            </div>
            
            <div className="mt-8 text-center md:hidden">
              <Link 
                to="/stories"
                className="inline-flex items-center gap-2 text-white/50 hover:text-white text-sm transition-colors"
              >
                View All Stories
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-24 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-8 md:px-12 text-center">
          <h2 className="font-serif text-4xl md:text-5xl font-light text-white mb-6">
            Interested in {service.name}?
          </h2>
          <p className="text-white/40 mb-12 max-w-md mx-auto">
            Let&apos;s discuss how I can help bring your vision to life.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-4 px-12 py-5 border border-white/20 text-white/90 text-sm tracking-[0.25em] uppercase hover:bg-white hover:text-black transition-all duration-500 group"
          >
            <span>Get a Quote</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-500" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetailPage;
